import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAlert } from '../../../context/alertContext';
import { UserContext } from '../../../context/authContext';
import {
  buildEntityAssignmentMetaData as buildEntityAssignmentData,
  type EntityAssignmentMetaDataSource,
  type PerspectivePartyType
} from './helpers/buildEntityAssignmentMetaData';
import {
  Alert,
  Box,
  Button,
  classNames,
  Container,
  Header,
  Spinner
} from './viewerSharedUi';

type EntitySectionKey = 'party-1' | 'party-2' | 'unknown' | 'ignore';
type PointOfView = PerspectivePartyType;

interface EntityAssignmentRecord extends EntityAssignmentMetaDataSource {
  id: string;
}

interface EntityAssignerProps {
  documentId?: string | null;
}

const POINT_OF_VIEW_OPTIONS: PointOfView[] = ['Party 1', 'Party 2'];

const SECTION_CONFIG: Array<{
  description: string;
  emptyText: string;
  entityType: string;
  key: EntitySectionKey;
  title: string;
}> = [
  {
    description: 'Primary first-party references.',
    emptyText: 'Drop entities here to assign them to Party 1.',
    entityType: 'Party 1',
    key: 'party-1',
    title: 'Party 1'
  },
  {
    description: 'Primary second-party references.',
    emptyText: 'Drop entities here to assign them to Party 2.',
    entityType: 'Party 2',
    key: 'party-2',
    title: 'Party 2'
  },
  {
    description: 'Unassigned, ambiguous, or external entities.',
    emptyText: 'Drop entities here to keep them as unknown.',
    entityType: 'Unknown',
    key: 'unknown',
    title: 'Other / Unknown'
  },
  {
    description: 'Entities to exclude from assignment.',
    emptyText: 'Drop entities here to ignore them.',
    entityType: 'Ignore',
    key: 'ignore',
    title: 'Ignore'
  }
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toStringValue = (value: unknown) =>
  typeof value === 'string' ? value : '';

const toNumberValue = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const getErrorMessage = (
  error: unknown,
  fallbackMessage = 'Unable to load entities for this document.'
) => {
  if (axios.isAxiosError(error)) {
    return (
      (typeof error.response?.data?.message === 'string'
        ? error.response.data.message
        : '') ||
      error.message ||
      fallbackMessage
    );
  }

  if (error instanceof Error) {
    return error.message || fallbackMessage;
  }

  return fallbackMessage;
};

const getSectionKeyForEntityType = (entityType: string): EntitySectionKey => {
  const normalizedEntityType = entityType.trim().toLowerCase();

  if (normalizedEntityType === 'party 1') {
    return 'party-1';
  }

  if (normalizedEntityType === 'party 2') {
    return 'party-2';
  }

  if (normalizedEntityType === 'ignore') {
    return 'ignore';
  }

  return 'unknown';
};

const normalizeEntityAssignments = (
  value: unknown
): EntityAssignmentRecord[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => {
        if (!isRecord(entry)) {
          return null;
        }

        const entityName = toStringValue(entry.entityName).trim();
        const genericEntityName = toStringValue(entry.genericEntityName).trim();
        const entityType = toStringValue(entry.entityType).trim() || 'Unknown';

        if (!entityName && !genericEntityName) {
          return null;
        }

        return {
          id: [
            entityName || 'entity',
            genericEntityName || 'generic',
            entityType,
            String(index)
          ].join('::'),
          confidenceScore: toNumberValue(entry.confidenceScore),
          entityName,
          entityType,
          genericEntityName
        } satisfies EntityAssignmentRecord;
      })
      .filter((entry): entry is EntityAssignmentRecord => Boolean(entry));
  }

  if (!isRecord(value)) {
    return [];
  }

  const nestedKeys = [
    'result',
    'documentMetaData',
    'entityAssignments',
    'entities',
    'entitiesDetected',
    'data'
  ] as const;

  for (const nestedKey of nestedKeys) {
    const normalizedNestedValue = normalizeEntityAssignments(value[nestedKey]);

    if (normalizedNestedValue.length > 0) {
      return normalizedNestedValue;
    }
  }

  return [];
};

const EntityAssigner = ({ documentId }: EntityAssignerProps) => {
  const { auth } = useContext(UserContext);
  const { setAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const resolvedDocumentId = documentId ?? searchParams.get('documentId');
  const [pointOfView, setPointOfView] = useState<PointOfView>('Party 1');
  const [assignments, setAssignments] = useState<EntityAssignmentRecord[]>([]);
  const [draggedEntityId, setDraggedEntityId] = useState<string | null>(null);
  const [dragOverSectionKey, setDragOverSectionKey] =
    useState<EntitySectionKey | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [taskToken, setTaskToken] = useState<string>('');
  const pointOfViewSectionKey: EntitySectionKey =
    pointOfView === 'Party 1' ? 'party-1' : 'party-2';
  const counterpartySectionKey: EntitySectionKey =
    pointOfView === 'Party 1' ? 'party-2' : 'party-1';

  const handleApproveEntityAssignment = async () => {
    if (!resolvedDocumentId) {
      setAlert(
        'Missing document id. Unable to save entity assignments.',
        'error',
        4
      );
      return;
    }

    console.log('assigments', assignments);
    const payload = buildEntityAssignmentData({
      assignments,
      perspectivePartyType: pointOfView
    });

    console.log('approved entity assignment payload', payload);
    setSaveLoading(true);

    try {
      if (!payload || !taskToken) {
        throw new Error('Missing Information');
      }
      await axios.post(
        `${process.env.REACT_APP_KORU_API}/document-metadata?type=saveDocumentMetaData`,
        {
          documentId: resolvedDocumentId,
          metaData: payload,
          taskToken
        },
        auth
      );
      setAlert('Entity assignments saved.', 'success', 3);
    } catch (saveError) {
      setAlert(
        getErrorMessage(saveError, 'Unable to save entity assignments.'),
        'error',
        4
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const loadAssignments = useCallback(
    async (activeCheck?: () => boolean) => {
      if (!resolvedDocumentId) {
        if (!activeCheck || activeCheck()) {
          setAssignments([]);
          setError('No documentId was provided in the URL.');
          setLoading(false);
        }
        return;
      }

      if (!activeCheck || activeCheck()) {
        setLoading(true);
        setError('');
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_KORU_API}/document-metadata?type=getDocumentMetaData`,
          { documentId: resolvedDocumentId },
          auth
        );
        const nextAssignments = normalizeEntityAssignments(
          response.data.metaData
        );
        if (!activeCheck || activeCheck()) {
          setAssignments(nextAssignments);
        }
        const tempToken = response.data.taskToken ?? '';
        setTaskToken(tempToken);
      } catch (loadError) {
        if (!activeCheck || activeCheck()) {
          setAssignments([]);
          setError(getErrorMessage(loadError));
        }
      } finally {
        if (!activeCheck || activeCheck()) {
          setLoading(false);
        }
      }
    },
    [auth, resolvedDocumentId]
  );

  useEffect(() => {
    let isActive = true;

    void loadAssignments(() => isActive);

    return () => {
      isActive = false;
    };
  }, [loadAssignments]);

  const assignmentsBySection = useMemo(() => {
    const nextMap: Record<EntitySectionKey, EntityAssignmentRecord[]> = {
      ignore: [],
      'party-1': [],
      'party-2': [],
      unknown: []
    };

    assignments.forEach((assignment) => {
      nextMap[getSectionKeyForEntityType(assignment.entityType)].push(
        assignment
      );
    });

    return nextMap;
  }, [assignments]);

  const moveAssignmentToSection = (
    assignmentId: string,
    targetSectionKey: EntitySectionKey
  ) => {
    const targetEntityType = SECTION_CONFIG.find(
      ({ key }) => key === targetSectionKey
    )?.entityType;

    if (!targetEntityType) {
      return;
    }

    setAssignments((previousValue) => {
      const draggedAssignment = previousValue.find(
        ({ id }) => id === assignmentId
      );

      if (!draggedAssignment) {
        return previousValue;
      }

      const nextAssignment = {
        ...draggedAssignment,
        entityType: targetEntityType
      };
      const remainingAssignments = previousValue.filter(
        ({ id }) => id !== assignmentId
      );

      return [...remainingAssignments, nextAssignment];
    });
  };

  const renderAssignmentCard = (assignment: EntityAssignmentRecord) => (
    <div
      key={assignment.id}
      draggable
      className={classNames(
        'cursor-grab rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition active:cursor-grabbing',
        draggedEntityId === assignment.id
          ? 'opacity-60 ring-2 ring-sky-300'
          : ''
      )}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', assignment.id);
        setDraggedEntityId(assignment.id);
      }}
      onDragEnd={() => {
        setDraggedEntityId(null);
        setDragOverSectionKey(null);
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900">
            {assignment.entityName || 'Unnamed entity'}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {assignment.genericEntityName || 'No generic label'}
          </div>
        </div>
        <div className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600">
          {(assignment.confidenceScore * 100).toFixed(0)}%
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <Container
        header={
          <Header
            variant="h2"
            description="Drag entity cards between sections to reassign their entity type."
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  loading={loading}
                  onClick={() => void loadAssignments()}
                >
                  Reload Entities
                </Button>
                <Button
                  loading={saveLoading}
                  disabled={loading}
                  onClick={() => void handleApproveEntityAssignment()}
                  variant="primary"
                >
                  Approve Entity Assignment
                </Button>
              </div>
            }
          >
            Entity Assigner
          </Header>
        }
      >
        <div className="space-y-4 p-4">
          {resolvedDocumentId ? (
            <Box color="text-body-secondary">
              Document ID: {resolvedDocumentId}
            </Box>
          ) : null}

          {error ? <Alert type="error">{error}</Alert> : null}

          {loading ? (
            <div className="flex min-h-[18rem] flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 px-6 py-10 text-center">
              <Spinner size="large" className="text-slate-500" />
              <div className="text-sm font-medium text-slate-700">
                Loading entity assignments
              </div>
              <div className="text-sm text-slate-500">
                Fetching document metadata for this document.
              </div>
            </div>
          ) : null}

          {!loading ? (
            <>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Our point of view
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      Choose which party represents us. The matching section is
                      marked as our side and the opposite party is marked as the
                      counterparty.
                    </p>
                  </div>
                  <div
                    aria-label="Select point of view"
                    className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
                    role="group"
                  >
                    {POINT_OF_VIEW_OPTIONS.map((option) => {
                      const isSelected = pointOfView === option;

                      return (
                        <Button
                          key={option}
                          aria-pressed={isSelected}
                          className={classNames(
                            'h-10 rounded-lg border-0 px-4',
                            isSelected
                              ? 'bg-slate-900 text-white hover:bg-slate-900'
                              : 'bg-transparent text-slate-600 hover:bg-slate-100'
                          )}
                          onClick={() => setPointOfView(option)}
                        >
                          {option}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {assignments.length === 0 ? (
                <Box color="text-body-secondary">
                  No entities were returned for this document.
                </Box>
              ) : null}

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                {SECTION_CONFIG.map(
                  ({ description, emptyText, key, title }) => {
                    const sectionAssignments = assignmentsBySection[key];
                    const isDragTarget = dragOverSectionKey === key;
                    const isPointOfViewSection = key === pointOfViewSectionKey;
                    const isCounterpartySection =
                      key === counterpartySectionKey;

                    return (
                      <section
                        key={key}
                        className={classNames(
                          'min-h-[16rem] rounded-2xl border bg-slate-50/70 p-3 transition',
                          isDragTarget
                            ? 'border-sky-400 ring-2 ring-sky-200'
                            : isPointOfViewSection
                              ? 'border-emerald-300 bg-emerald-50/60'
                              : isCounterpartySection
                                ? 'border-amber-300 bg-amber-50/60'
                                : 'border-slate-200'
                        )}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = 'move';
                          setDragOverSectionKey(key);
                        }}
                        onDragLeave={(event) => {
                          if (
                            event.currentTarget.contains(
                              event.relatedTarget as Node
                            )
                          ) {
                            return;
                          }

                          setDragOverSectionKey((currentValue) =>
                            currentValue === key ? null : currentValue
                          );
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          const assignmentId =
                            event.dataTransfer.getData('text/plain') ||
                            draggedEntityId;

                          if (assignmentId) {
                            moveAssignmentToSection(assignmentId, key);
                          }

                          setDraggedEntityId(null);
                          setDragOverSectionKey(null);
                        }}
                      >
                        <div className="mb-3 flex items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-sm font-semibold text-slate-900">
                                {title}
                              </h3>
                              {isPointOfViewSection ? (
                                <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                                  Our side
                                </span>
                              ) : null}
                              {isCounterpartySection ? (
                                <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                                  Counterparty
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-1 text-xs text-slate-500">
                              {description}
                            </p>
                          </div>
                          <div className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">
                            {sectionAssignments.length}
                          </div>
                        </div>

                        <div className="space-y-3">
                          {sectionAssignments.length > 0 ? (
                            sectionAssignments.map(renderAssignmentCard)
                          ) : (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-3 py-6 text-center text-xs text-slate-500">
                              {emptyText}
                            </div>
                          )}
                        </div>
                      </section>
                    );
                  }
                )}
              </div>
            </>
          ) : null}
        </div>
      </Container>
    </div>
  );
};

export default EntityAssigner;
